import React, { createContext, useCallback, useContext, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ToastContext = createContext(null);
let nextId = 1;

// Replacement for alert() — most call sites in this app use alert() purely
// to report a failed action ("Failed: " + err.message). toast(message) is a
// near-drop-in swap that doesn't block the tab the way alert() does, and
// looks like the rest of the app instead of browser chrome.
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, opts = {}) => {
    const id = nextId++;
    const variant = opts.variant || 'error';
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => dismiss(id), opts.duration ?? 6000);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed bottom-5 right-5 z-[2100] flex flex-col gap-2 max-w-sm w-full px-4 sm:px-0">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-start gap-2.5 rounded-xl shadow-lg px-4 py-3 text-sm ${
              t.variant === 'success'
                ? 'bg-green-600 text-white'
                : 'bg-gray-900 text-white'
            }`}
          >
            <p className="flex-1 whitespace-pre-wrap">{t.message}</p>
            <button type="button" onClick={() => dismiss(t.id)} className="text-white/70 hover:text-white flex-shrink-0">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
